import { useState, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@material-ui/core/Button';

import companyLogo from 'logo/logo-full.svg';
import { Stack } from '@mui/material';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import { initialValues, validate } from './form';
import Loader from '~/ui/components/common/Loader';
import Input from '~/ui/components/inputs/Input';
import { IVerifyCode } from '~/services/api/auth/types';

import styles from './VerifyCode.module.scss';
import ResendCode from '../ResendCode';
import { MfaType } from '~/services/api/enums';

const VerifyCode = (): ReactElement => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IVerifyCode>({
    resolver: validate,
    defaultValues: initialValues,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const { recipient } = useStoreState(store => store.auth.mfaRequired);

  const { onResendCode, onVerifyCode } = useStoreActions(actions => actions.auth);

  const resendCode = async (mfaTypeId: MfaType) => {
    setIsLoading(true);
    setServerError('');

    try {
      await onResendCode(mfaTypeId);
    } catch (e) {
      setServerError(extractErrorMessage(e));
    }

    setIsLoading(false);
  };

  const onSubmit = async (values: IVerifyCode) => {
    setIsLoading(true);
    setServerError('');

    try {
      await onVerifyCode(values);
    } catch (e) {
      setServerError(extractErrorMessage(e));
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <div className={styles.logo}>
          <img src={companyLogo} alt="Logo" />
        </div>
        <div>A 6-digit code was sent to {recipient}.</div>
        <div className={styles.row}>
          <Input
            name="code"
            register={register}
            errors={errors}
            size="medium"
            label="Authentication Code"
            required
            placeholder="Enter Code"
          />
        </div>
        <div className={styles.row}>
          <Button
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            className={styles.btn}
            disabled={isLoading}
          >
            Confirm
          </Button>
        </div>
        {isLoading && <Loader />}

        <ResendCode onResendCode={resendCode} />

        {serverError && <div className={styles.serverError}>{serverError}</div>}
      </Stack>
    </form>
  );
};

export default VerifyCode;
